import type {
  QueueStatus,
  QueueStep,
  TQueue,
} from "../../../../context/queue/types";

export class StoreAdapter {
  static toOutputDTO(res: any[]): TQueue[] {
    const oneDayAgo = Date.now() - 1 * 24 * 60 * 60 * 1000;

    const mapStatus = (code: number): QueueStatus => {
      switch (code) {
        case 0:
          return "idle";
        case 1:
          return "loading";
        case 2:
          return "success";
        case 3:
          return "error";
        default:
          return "unknown";
      }
    };

    const mapCombinedStatus = (
      fileStatus: number,
      stepCode: number,
      stepName: "upload" | "store"
    ): QueueStatus => {
      if (fileStatus === 3) {
        return "error";
      }
      if (stepName === "store" && fileStatus === 2 && stepCode === 0) {
        return "unknown";
      }
      return mapStatus(stepCode);
    };

    const sorted = res
      .filter((e) => new Date(e.upload_at).getTime() >= oneDayAgo)
      .sort(
        (a, b) =>
          new Date(b.upload_at).getTime() - new Date(a.upload_at).getTime()
      );

    const allDtos: TQueue[] = sorted.map((e: any) => {
      // 2 フェーズ取込 (upload_id あり) では「アップロード」= presigned PUT + API-1 (raw parquet 生成)。
      // API-1 の起動時点で upload_status は COMPLETED になるため、進捗は raw_status で見る。
      // raw_status=WAITING は「起動直後でまだ進捗が書かれていない」状態なので loading 扱いにする。
      const isTwoPhase = Boolean(e.upload_id);
      const rawCode = e.raw_status ?? 0;
      const uploadStatus = mapCombinedStatus(
        e.status,
        isTwoPhase ? (rawCode === 0 ? 1 : rawCode) : e.upload_status,
        "upload"
      );
      const storeStatus = mapCombinedStatus(e.status, e.store_status, "store");
      const currentStep = uploadStatus === "success" ? 1 : 0;

      const steps: QueueStep[] = [
        { name: "upload", status: uploadStatus },
        { name: "store", status: storeStatus },
      ];

      const formatDate = (date: string): Date => {
        const parsedDate = new Date(date);
        const localTs =
          parsedDate.getTime() - parsedDate.getTimezoneOffset() * 60000;
        return new Date(localTs);
      };

      return {
        queueId: e.data_id,
        currentStep,
        steps,
        meta: {
          client_id: e.client_id,
          login_user_id: e.login_user_id,
          store_type: e.store_type,
          concent_type: e.concent_type,
          // 2 フェーズ取込の job キー (旧 /upload 経由の行では null)。
          // ある = 取込実行が API-2 (derive) / 無い = 従来の /store。
          upload_id: e.upload_id ?? null,
          raw_status: e.raw_status ?? null,
          store_status: e.store_status ?? null,
          // 4 段フローの現在ステップ (サーバ導出)。これがあるので段の判定に GET /ingest
          // (raw parquet の列統計フルスキャン) を待つ必要がない。旧 /upload 由来・無効化済は null。
          ingest_step: e.ingest_step ?? null,
          detected_finest: e.detected_finest ?? null,
          data: {
            id: e.data_id,
            name: e.name,
            size: e.size,
            desc: e.description,
            id_codes: e.id_codes,
            error_code: e.error_code,
          },
          timestamps: {
            updated_at: e.updated_at ? formatDate(e.updated_at) : null,
            upload_at: e.upload_at ? formatDate(e.upload_at) : null,
            consent_at: e.consent_at ? formatDate(e.consent_at) : null,
          },
        },
      };
    });

    const finalStatuses: QueueStatus[] = ["success", "error", "unknown"];
    const latestByStatus: Partial<Record<QueueStatus, TQueue>> = {};
    const others: TQueue[] = [];

    for (const dto of allDtos) {
      const status = dto.steps[dto.currentStep].status;
      if (finalStatuses.includes(status)) {
        if (!latestByStatus[status]) {
          latestByStatus[status] = dto;
        }
      } else {
        others.push(dto);
      }
    }

    return [
      ...others,
      ...finalStatuses
        .map((s) => latestByStatus[s])
        .filter((dto): dto is TQueue => Boolean(dto)),
    ];
  }
}
